import { createContext, useContext, useState, useCallback, useMemo, type ReactNode } from 'react';
import { useAppStore } from '@repletoryreach/sdk-utils/store';
import { CATALOGS, type Locale } from './catalog';

// W39: admin-console i18n. App-local copy of the tenant-ui pattern
// (apps/tenant-ui/src/i18n/I18nProvider.tsx); deliberately NOT lifted into
// sdk-ui: each console owns its catalog and storage key.

const STORAGE_KEY = 'rr.admin.locale';

/** Values substituted into `{{name}}` placeholders. */
export type TranslateVars = Record<string, string | number>;

/**
 * `t(key)` · `t(key, vars)` · `t(key, fallback)` · `t(key, fallback, vars)`.
 * Missing keys fall back locale → en → explicit fallback → raw key, never throw.
 */
export type Translate = (key: string, fallbackOrVars?: string | TranslateVars, vars?: TranslateVars) => string;

interface I18nContextValue {
  locale: Locale;
  setLocale: (l: Locale) => void;
  t: Translate;
}

const I18nContext = createContext<I18nContextValue | null>(null);

function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && value in CATALOGS;
}

function readInitialLocale(): Locale {
  if (typeof window === 'undefined') return 'en';
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (isLocale(stored)) return stored;
  } catch { /* storage unavailable: fall through */ }
  // The sdk-utils store carries the profile/JWT locale (may be a BCP-47 tag).
  const fromStore = useAppStore.getState().locale?.slice(0, 2);
  if (isLocale(fromStore)) return fromStore;
  const browser = window.navigator.language.slice(0, 2);
  if (isLocale(browser)) return browser;
  return 'en';
}

/** Replace `{{name}}` tokens; unknown tokens are left verbatim. */
export function interpolate(template: string, vars?: TranslateVars): string {
  if (!vars) return template;
  return template.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, name: string) =>
    Object.prototype.hasOwnProperty.call(vars, name) ? String(vars[name]) : match
  );
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(readInitialLocale);

  const setLocale = useCallback((l: Locale) => {
    setLocaleState(l);
    try { window.localStorage.setItem(STORAGE_KEY, l); } catch { /* ignore */ }
    // Mirror into the sdk-utils app store so DatesProvider / number formatting follow the picker.
    useAppStore.getState().setLocale(l);
  }, []);

  const t = useCallback<Translate>(
    (key, fallbackOrVars, vars) => {
      const fallback = typeof fallbackOrVars === 'string' ? fallbackOrVars : undefined;
      const substitutions = typeof fallbackOrVars === 'object' ? fallbackOrVars : vars;
      const catalog = CATALOGS[locale];
      const raw = catalog[key] ?? CATALOGS.en[key] ?? fallback ?? key;
      return interpolate(raw, substitutions);
    },
    [locale]
  );

  const value = useMemo<I18nContextValue>(() => ({ locale, setLocale, t }), [locale, setLocale, t]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used inside <I18nProvider>');
  return ctx;
}

/** Convenience hook for the common case: just the translate function. */
export function useT(): Translate {
  return useI18n().t;
}
